<template>
  <div class="discounts-settings-container">
    <!-- Header -->
    <div class="discounts-header">
      <div class="header-icon">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="19" y1="5" x2="5" y2="19"/>
          <circle cx="6.5" cy="6.5" r="2.5"/>
          <circle cx="17.5" cy="17.5" r="2.5"/>
        </svg>
      </div>
      <div>
        <h1 class="page-title">Kedvezmények & Egyedi Havi Díjak</h1>
        <p class="page-subtitle">
          Táncosok havi díjának kézi beállítása és csoport-hozzárendelések áttekintése. A beállított egyedi díj felülírja a csoportok összegét.
        </p>
      </div>
    </div>

    <!-- Stats Bar -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon stat-icon--blue">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
          </svg>
        </div>
        <div>
          <div class="stat-value">{{ dancers.length }}</div>
          <div class="stat-label">Összes táncos</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon stat-icon--purple">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
          </svg>
        </div>
        <div>
          <div class="stat-value">{{ multiGroupCount }}</div>
          <div class="stat-label">Több csoportos táncos</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon stat-icon--green">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
          </svg>
        </div>
        <div>
          <div class="stat-value">{{ discountedCount }}</div>
          <div class="stat-label">Egyedi díjjal beállítva</div>
        </div>
      </div>
    </div>

    <!-- Toolbar: Search & Filter Tabs -->
    <div class="table-toolbar">
      <div class="search-wrap">
        <svg class="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="8"/>
          <line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input 
          type="text" 
          v-model="searchQuery" 
          placeholder="Keresés táncos neve vagy csoport szerint..." 
          class="search-input"
        />
        <button v-if="searchQuery" class="clear-search" @click="searchQuery = ''">&times;</button>
      </div>

      <div class="filter-tabs">
        <button 
          class="filter-tab" 
          :class="{ active: activeFilter === 'all' }" 
          @click="activeFilter = 'all'"
        >
          Mind ({{ dancers.length }})
        </button>
        <button 
          class="filter-tab" 
          :class="{ active: activeFilter === 'multigroup' }" 
          @click="activeFilter = 'multigroup'"
        >
          Többcsoportosok ({{ multiGroupCount }})
        </button>
        <button 
          class="filter-tab" 
          :class="{ active: activeFilter === 'discounted' }" 
          @click="activeFilter = 'discounted'"
        >
          Egyedi díjasok ({{ discountedCount }})
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="spinner-border text-primary" role="status"></div>
      <p class="mt-2 text-muted">Táncosok és csoportok betöltése...</p>
    </div>

    <!-- Main Table -->
    <div v-else-if="filteredDancers.length > 0" class="table-card">
      <div class="table-responsive">
        <table class="modern-table">
          <thead>
            <tr>
              <th style="min-width: 180px;">Táncos</th>
              <th style="min-width: 260px;">Csoportok (Kiemelve)</th>
              <th style="min-width: 130px; text-align: right;">Alap díjösszeg</th>
              <th style="min-width: 200px;">Egyedi Havi Díj (HUF)</th>
              <th style="min-width: 140px; text-align: right;">Hatályos havi díj</th>
              <th style="min-width: 110px; text-align: center;">Művelet</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="dancer in filteredDancers" 
              :key="dancer.child_id"
              :class="{ 'row-discounted': dancer.has_discount, 'row-multigroup': dancer.groups.length > 1 }"
            >
              <!-- Dancer column -->
              <td class="dancer-cell">
                <div class="dancer-info-wrap">
                  <div class="dancer-avatar">
                    {{ getInitials(dancer.name) }}
                  </div>
                  <div>
                    <div class="dancer-name">{{ dancer.name }}</div>
                    <div class="dancer-sub">
                      <span class="dancer-id-tag">#{{ dancer.child_id }}</span>
                      <span v-if="dancer.groups.length > 1" class="badge-multi">
                        {{ dancer.groups.length }} csoport
                      </span>
                    </div>
                  </div>
                </div>
              </td>

              <!-- Groups column with visual highlights -->
              <td class="groups-cell">
                <div v-if="dancer.groups && dancer.groups.length > 0" class="groups-list">
                  <div 
                    v-for="g in dancer.groups" 
                    :key="g.group_id" 
                    class="group-tag"
                    :class="{ 'group-tag--primary': g.is_primary }"
                  >
                    <span class="group-tag-name">
                      <span v-if="g.is_primary" class="primary-indicator" title="Elsődleges csoport">★</span>
                      {{ g.group_name }}
                    </span>
                    <span class="group-tag-price">
                      {{ formatPrice(g.monthly_price) }}
                    </span>
                  </div>
                </div>
                <span v-else class="no-group-text">Nincs csoporthoz rendelve</span>
              </td>

              <!-- Default total (sum of groups) -->
              <td class="default-price-cell text-end">
                <div class="default-price" :class="{ 'price-strikethrough': dancer.has_discount }">
                  {{ formatPrice(dancer.default_monthly_total) }}
                </div>
                <small v-if="dancer.has_discount" class="text-muted d-block" style="font-size: 0.72rem;">
                  Alap összeg
                </small>
              </td>

              <!-- Custom Unique Price Input -->
              <td class="override-cell">
                <div class="input-action-group">
                  <div class="input-with-suffix">
                    <input 
                      type="number" 
                      min="0" 
                      step="any" 
                      v-model.number="dancer.edit_override" 
                      placeholder="pl. 18000"
                      class="override-input"
                      @keyup.enter="saveOverride(dancer)"
                    />
                    <span class="input-suffix">Ft</span>
                  </div>
                  <button 
                    v-if="dancer.has_discount" 
                    class="btn-reset-override" 
                    @click="resetOverride(dancer)"
                    title="Egyedi díj törlése (visszaállítás a csoportok összegére)"
                    :disabled="dancer.saving"
                  >
                    ↺
                  </button>
                </div>
                <div v-if="dancer.has_discount" class="discount-diff-hint text-success">
                  Különbözet: {{ formatDiff(dancer.default_monthly_total, dancer.override_price) }}
                </div>
              </td>

              <!-- Effective Monthly Price -->
              <td class="effective-cell text-end">
                <div class="effective-badge" :class="dancer.has_discount ? 'effective-custom' : 'effective-default'">
                  {{ formatPrice(dancer.effective_monthly_price) }}
                </div>
                <small class="d-block text-muted mt-0.5" style="font-size: 0.72rem;">
                  {{ dancer.has_discount ? 'Egyedi ár' : 'Csoportok összege' }}
                </small>
              </td>

              <!-- Action button -->
              <td class="action-cell text-center">
                <button 
                  class="btn-save-row"
                  @click="saveOverride(dancer)"
                  :disabled="dancer.saving"
                  :class="{ 'btn-save-row--success': dancer.justSaved }"
                >
                  <span v-if="dancer.saving" class="spinner-border spinner-border-sm me-1" role="status"></span>
                  <span v-else-if="dancer.justSaved">✓ Mentve</span>
                  <span v-else>Mentés</span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Empty search state -->
    <div v-else class="empty-state">
      <div class="empty-icon">🔍</div>
      <h3>Nincs találat a keresési feltételekre</h3>
      <p class="text-muted">Próbálj más keresőszót megadni vagy módosítsd a szűrőket.</p>
      <button class="btn btn-sm btn-outline-primary rounded-pill px-3" @click="resetFilters">Szűrők törlése</button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import toastMixin from '../mixins/toast.js';

export default {
  name: 'DiscountsSettingsItem',
  mixins: [toastMixin],
  data() {
    return {
      dancers: [],
      loading: true,
      searchQuery: '',
      activeFilter: 'all' // 'all', 'multigroup', 'discounted'
    };
  },
  computed: {
    isAdmin() {
      return this.$isAdmin ? this.$isAdmin() : (this.$store && this.$store.getters && this.$store.getters.isAdmin);
    },
    multiGroupCount() {
      return this.dancers.filter(d => d.groups && d.groups.length > 1).length;
    },
    discountedCount() {
      return this.dancers.filter(d => d.has_discount).length;
    },
    filteredDancers() {
      let list = this.dancers;

      // Filter tabs
      if (this.activeFilter === 'multigroup') {
        list = list.filter(d => d.groups && d.groups.length > 1);
      } else if (this.activeFilter === 'discounted') {
        list = list.filter(d => d.has_discount);
      }

      // Search query
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase().trim();
        list = list.filter(d => {
          const matchName = (d.name || '').toLowerCase().includes(q);
          const matchId = String(d.child_id).includes(q);
          const matchGroup = (d.groups || []).some(g => (g.group_name || '').toLowerCase().includes(q));
          return matchName || matchId || matchGroup;
        });
      }

      return list;
    }
  },
  created() {
    this.loadDancers();
  },
  methods: {
    async loadDancers() {
      this.loading = true;
      try {
        const res = await axios.get(this.$BACKEND_HOST + '/api/billing/discounts/dancers');
        const list = res.data.dancers || [];
        this.dancers = list.map(d => ({
          ...d,
          edit_override: d.override_price != null ? d.override_price : null,
          saving: false,
          justSaved: false
        }));
      } catch (err) {
        console.error('Error loading dancers for discounts:', err);
        this.showToast('Hiba a táncosok betöltésekor', 'error');
      } finally {
        this.loading = false;
      }
    },
    async saveOverride(dancer) {
      if (!dancer) return;
      dancer.saving = true;
      try {
        const val = dancer.edit_override !== null && dancer.edit_override !== '' 
          ? Number(dancer.edit_override) 
          : null;

        await axios.put(this.$BACKEND_HOST + '/api/billing/override', {
          child_id: dancer.child_id,
          value: (val !== null && val > 0) ? val : null
        });

        if (val !== null && val > 0) {
          dancer.override_price = val;
          dancer.effective_monthly_price = val;
          dancer.has_discount = true;
          this.showToast(`${dancer.name} egyedi havi díja mentve: ${this.formatPrice(val)}`, 'success');
        } else {
          dancer.override_price = null;
          dancer.edit_override = null;
          dancer.effective_monthly_price = dancer.default_monthly_total;
          dancer.has_discount = false;
          this.showToast(`${dancer.name} egyedi díja törölve, alap csoportösszeg érvényes.`, 'info');
        }

        dancer.justSaved = true;
        setTimeout(() => { dancer.justSaved = false; }, 2000);
      } catch (err) {
        console.error('Error saving override:', err);
        this.showToast('Hiba a mentés során: ' + (err.response?.data?.message || err.message), 'error');
      } finally {
        dancer.saving = false;
      }
    },
    async resetOverride(dancer) {
      if (!dancer) return;
      dancer.edit_override = null;
      await this.saveOverride(dancer);
    },
    resetFilters() {
      this.searchQuery = '';
      this.activeFilter = 'all';
    },
    getInitials(name) {
      if (!name) return 'T';
      const parts = name.trim().split(/\s+/);
      if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
      return (parts[0][0] + parts[1][0]).toUpperCase();
    },
    formatPrice(num) {
      if (num === null || num === undefined || isNaN(num)) return '0 Ft';
      return Math.round(num).toLocaleString('hu-HU') + ' Ft';
    },
    formatDiff(defaultTotal, overridePrice) {
      if (overridePrice == null) return '';
      const diff = defaultTotal - overridePrice;
      if (diff > 0) return `-${this.formatPrice(diff)} kedvezmény`;
      if (diff < 0) return `+${this.formatPrice(-diff)} felár`;
      return 'Egyenlő az alapdíjjal';
    }
  }
};
</script>

<style scoped>
.discounts-settings-container {
  padding: 1.25rem;
  max-width: 1200px;
  margin: 0 auto;
}

/* Header */
.discounts-header {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  margin-bottom: 1.75rem;
}

.header-icon {
  width: 60px;
  height: 60px;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(37, 99, 235, 0.12), rgba(59, 130, 246, 0.2));
  color: var(--primary-blue, #2563eb);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.1);
  transition: all 0.3s ease;
}

.page-title {
  font-size: 1.6rem;
  font-weight: 800;
  color: var(--color-heading, #0f172a);
  margin-bottom: 0.25rem;
  letter-spacing: -0.02em;
}

.page-subtitle {
  color: var(--color-text-muted, #64748b);
  font-size: 0.92rem;
  margin-bottom: 0;
  line-height: 1.45;
}

/* Stats */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat-card {
  background: var(--card-bg, #ffffff);
  border: 1px solid var(--color-border, #e2e8f0);
  border-radius: 14px;
  padding: 1rem 1.25rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.stat-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-icon--blue {
  background: rgba(59, 130, 246, 0.12);
  color: #2563eb;
}

.stat-icon--purple {
  background: rgba(147, 51, 234, 0.12);
  color: #7e22ce;
}

.stat-icon--green {
  background: rgba(16, 185, 129, 0.12);
  color: #059669;
}

.stat-value {
  font-size: 1.4rem;
  font-weight: 800;
  color: var(--color-heading, #0f172a);
  line-height: 1.1;
}

.stat-label {
  font-size: 0.78rem;
  color: var(--color-text-muted, #64748b);
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.5px;
  margin-top: 0.2rem;
}

/* Toolbar */
.table-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.search-wrap {
  position: relative;
  flex: 1;
  min-width: 260px;
  max-width: 460px;
}

.search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-muted, #94a3b8);
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 0.6rem 2.2rem 0.6rem 2.5rem;
  border-radius: 100px;
  border: 1px solid var(--color-border, #cbd5e1);
  background: var(--card-bg, #ffffff);
  color: var(--color-text, #1e293b);
  font-size: 0.9rem;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s, background-color 0.2s;
}

.search-input::placeholder {
  color: var(--color-text-muted, #94a3b8);
}

.search-input:focus {
  border-color: var(--primary-blue, #2563eb);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.18);
}

.clear-search {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--color-text-muted, #94a3b8);
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  transition: color 0.2s ease;
}

.clear-search:hover {
  color: var(--color-heading, #0f172a);
}

.filter-tabs {
  display: flex;
  gap: 0.4rem;
  background: var(--color-background-soft, #f1f5f9);
  padding: 0.25rem;
  border-radius: 100px;
  border: 1px solid var(--color-border, #e2e8f0);
}

.filter-tab {
  border: none;
  background: transparent;
  padding: 0.45rem 1rem;
  border-radius: 100px;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--color-text-muted, #64748b);
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-tab:hover:not(.active) {
  color: var(--color-heading, #0f172a);
}

.filter-tab.active {
  background: var(--card-bg, #ffffff);
  color: var(--primary-blue, #2563eb);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

/* Table Card */
.table-card {
  background: var(--card-bg, #ffffff);
  border: 1px solid var(--color-border, #e2e8f0);
  border-radius: 16px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.table-responsive {
  overflow-x: auto;
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.modern-table th {
  background: var(--bg-secondary, #f8fafc);
  color: var(--color-text-muted, #64748b);
  font-size: 0.76rem;
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.6px;
  padding: 0.9rem 1.2rem;
  border-bottom: 2px solid var(--color-border, #e2e8f0);
}

.modern-table td {
  padding: 1rem 1.2rem;
  border-bottom: 1px solid var(--color-border, #f1f5f9);
  vertical-align: middle;
  color: var(--color-text, #1e293b);
}

.modern-table tbody tr:hover {
  background: var(--hover-bg, rgba(37, 99, 235, 0.035));
}

.row-discounted {
  background: rgba(16, 185, 129, 0.03);
}

.row-multigroup {
  border-left: 3px solid #6366f1;
}

/* Dancer cell */
.dancer-info-wrap {
  display: flex;
  align-items: center;
  gap: 0.85rem;
}

.dancer-avatar {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--primary-blue, #2563eb), #6366f1);
  color: #ffffff;
  font-weight: 700;
  font-size: 0.88rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.25);
}

.dancer-name {
  font-weight: 700;
  color: var(--color-heading, #0f172a);
  font-size: 0.96rem;
  line-height: 1.2;
}

.dancer-sub {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.2rem;
}

.dancer-id-tag {
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--color-text-muted, #64748b);
  background: var(--color-background-soft, #f1f5f9);
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
}

.badge-multi {
  background: rgba(99, 102, 241, 0.14);
  color: #4338ca;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.1rem 0.45rem;
  border-radius: 100px;
}

/* Groups List & Highlight Tags */
.groups-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.group-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  background: var(--bg-secondary, #f8fafc);
  border: 1px solid var(--color-border, #cbd5e1);
  color: var(--color-text, #1e293b);
  padding: 0.3rem 0.65rem;
  border-radius: 8px;
  font-size: 0.8rem;
  transition: transform 0.15s ease, border-color 0.15s ease;
}

.group-tag:hover {
  transform: translateY(-1px);
}

.group-tag--primary {
  background: rgba(37, 99, 235, 0.08);
  border-color: rgba(37, 99, 235, 0.28);
  color: var(--primary-blue, #1d4ed8);
}

.group-tag-name {
  font-weight: 600;
}

.primary-indicator {
  color: #f59e0b;
  margin-right: 2px;
}

.group-tag-price {
  font-weight: 700;
  color: #059669;
  background: rgba(16, 185, 129, 0.1);
  padding: 0.05rem 0.35rem;
  border-radius: 4px;
  font-size: 0.74rem;
}

.no-group-text {
  color: var(--color-text-muted, #94a3b8);
  font-style: italic;
  font-size: 0.82rem;
}

/* Default price cell */
.default-price {
  font-weight: 700;
  font-size: 0.95rem;
  color: var(--color-heading, #1e293b);
}

.price-strikethrough {
  text-decoration: line-through;
  color: var(--color-text-muted, #94a3b8);
  font-size: 0.88rem;
}

/* Override input */
.input-action-group {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.input-with-suffix {
  position: relative;
  display: inline-flex;
  align-items: center;
  width: 140px;
}

.override-input {
  width: 100%;
  padding: 0.45rem 1.8rem 0.45rem 0.65rem;
  border: 1px solid var(--color-border, #cbd5e1);
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.92rem;
  color: var(--color-heading, #0f172a);
  background: var(--card-bg, #ffffff);
  text-align: right;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s, background-color 0.2s;
}

.override-input:focus {
  border-color: var(--primary-blue, #2563eb);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.18);
}

.input-suffix {
  position: absolute;
  right: 8px;
  color: var(--color-text-muted, #94a3b8);
  font-size: 0.8rem;
  font-weight: 600;
  pointer-events: none;
}

.btn-reset-override {
  background: var(--color-background-soft, #f1f5f9);
  border: 1px solid var(--color-border, #cbd5e1);
  color: var(--color-text-muted, #64748b);
  width: 32px;
  height: 32px;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.btn-reset-override:hover {
  background: #fee2e2;
  border-color: #fca5a5;
  color: #dc2626;
}

.discount-diff-hint {
  font-size: 0.72rem;
  font-weight: 700;
  margin-top: 0.25rem;
  color: #059669;
}

/* Effective price cell */
.effective-badge {
  display: inline-block;
  padding: 0.35rem 0.75rem;
  border-radius: 8px;
  font-weight: 800;
  font-size: 0.96rem;
  letter-spacing: -0.01em;
}

.effective-custom {
  background: rgba(16, 185, 129, 0.12);
  color: #047857;
  border: 1px solid rgba(16, 185, 129, 0.25);
}

.effective-default {
  background: var(--bg-secondary, #f8fafc);
  color: var(--color-heading, #1e293b);
  border: 1px solid var(--color-border, #e2e8f0);
}

/* Action button */
.btn-save-row {
  background: var(--primary-blue, #2563eb);
  color: #ffffff;
  border: none;
  padding: 0.45rem 0.9rem;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 80px;
}

.btn-save-row:hover:not(:disabled) {
  background: var(--button-primary-hover, #1d4ed8);
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.3);
}

.btn-save-row:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-save-row--success {
  background: #059669 !important;
}

/* Loading & Empty State */
.loading-state,
.empty-state {
  text-align: center;
  padding: 3.5rem 1rem;
  background: var(--card-bg, #ffffff);
  border-radius: 16px;
  border: 1px solid var(--color-border, #e2e8f0);
  color: var(--color-text, #1e293b);
}

.empty-state h3 {
  color: var(--color-heading, #0f172a);
}

.empty-icon {
  font-size: 2.5rem;
  margin-bottom: 0.75rem;
}

/* ==========================================================================
   Dark Theme Specific Overrides
   ========================================================================== */
[data-theme="dark"] .header-icon {
  background: linear-gradient(135deg, rgba(37, 99, 235, 0.25), rgba(96, 165, 250, 0.2));
  color: #60a5fa;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
}

[data-theme="dark"] .stat-card {
  background: var(--card-bg, #1e293b);
  border-color: var(--color-border, #334155);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}

[data-theme="dark"] .stat-icon--blue {
  background: rgba(37, 99, 235, 0.22);
  color: #60a5fa;
}

[data-theme="dark"] .stat-icon--purple {
  background: rgba(168, 85, 247, 0.22);
  color: #c084fc;
}

[data-theme="dark"] .stat-icon--green {
  background: rgba(16, 185, 129, 0.22);
  color: #34d399;
}

[data-theme="dark"] .search-input {
  background: var(--color-background-soft, #1e293b);
  border-color: var(--color-border, #475569);
  color: var(--color-heading, #ffffff);
}

[data-theme="dark"] .search-input:focus {
  border-color: #60a5fa;
  box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.25);
}

[data-theme="dark"] .filter-tabs {
  background: var(--color-background-mute, #334155);
  border-color: var(--color-border, #475569);
}

[data-theme="dark"] .filter-tab.active {
  background: var(--card-bg, #1e293b);
  color: #60a5fa;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
  border: 1px solid var(--color-border, #475569);
}

[data-theme="dark"] .table-card {
  background: var(--card-bg, #1e293b);
  border-color: var(--color-border, #334155);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.35);
}

[data-theme="dark"] .modern-table th {
  background: rgba(15, 23, 42, 0.65);
  color: var(--color-text-muted, #94a3b8);
  border-bottom: 2px solid var(--color-border, #334155);
}

[data-theme="dark"] .modern-table td {
  border-bottom-color: rgba(255, 255, 255, 0.07);
}

[data-theme="dark"] .modern-table tbody tr:hover {
  background: rgba(59, 130, 246, 0.08);
}

[data-theme="dark"] .row-discounted {
  background: rgba(16, 185, 129, 0.07);
}

[data-theme="dark"] .row-multigroup {
  border-left: 3px solid #818cf8;
}

[data-theme="dark"] .dancer-id-tag {
  background: rgba(255, 255, 255, 0.08);
  color: var(--color-text-muted, #94a3b8);
}

[data-theme="dark"] .badge-multi {
  background: rgba(129, 140, 248, 0.22);
  color: #a5b4fc;
  border: 1px solid rgba(129, 140, 248, 0.35);
}

[data-theme="dark"] .group-tag {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.12);
  color: var(--color-text, #f1f5f9);
}

[data-theme="dark"] .group-tag--primary {
  background: rgba(37, 99, 235, 0.22);
  border-color: rgba(96, 165, 250, 0.35);
  color: #93c5fd;
}

[data-theme="dark"] .primary-indicator {
  color: #fbbf24;
}

[data-theme="dark"] .group-tag-price {
  color: #34d399;
  background: rgba(16, 185, 129, 0.2);
}

[data-theme="dark"] .override-input {
  background: var(--color-background, #0f172a);
  border-color: var(--color-border, #475569);
  color: var(--color-heading, #ffffff);
}

[data-theme="dark"] .override-input:focus {
  border-color: #60a5fa;
  box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.25);
}

[data-theme="dark"] .btn-reset-override {
  background: rgba(255, 255, 255, 0.08);
  border-color: var(--color-border, #475569);
  color: var(--color-text-muted, #94a3b8);
}

[data-theme="dark"] .btn-reset-override:hover {
  background: rgba(239, 68, 68, 0.22);
  border-color: rgba(239, 68, 68, 0.4);
  color: #fca5a5;
}

[data-theme="dark"] .discount-diff-hint {
  color: #34d399;
}

[data-theme="dark"] .effective-custom {
  background: rgba(16, 185, 129, 0.2);
  color: #34d399;
  border-color: rgba(16, 185, 129, 0.35);
}

[data-theme="dark"] .effective-default {
  background: rgba(255, 255, 255, 0.06);
  color: var(--color-heading, #f1f5f9);
  border-color: var(--color-border, #475569);
}

[data-theme="dark"] .btn-save-row {
  background: #2563eb;
}

[data-theme="dark"] .btn-save-row:hover:not(:disabled) {
  background: #3b82f6;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.45);
}

[data-theme="dark"] .loading-state,
[data-theme="dark"] .empty-state {
  background: var(--card-bg, #1e293b);
  border-color: var(--color-border, #334155);
}

/* Fallback for system dark mode */
@media (prefers-color-scheme: dark) {
  :root:not([data-theme="light"]) .header-icon {
    background: linear-gradient(135deg, rgba(37, 99, 235, 0.25), rgba(96, 165, 250, 0.2));
    color: #60a5fa;
  }
  :root:not([data-theme="light"]) .stat-card {
    background: var(--card-bg, #1e293b);
    border-color: var(--color-border, #334155);
  }
  :root:not([data-theme="light"]) .stat-icon--blue {
    background: rgba(37, 99, 235, 0.22);
    color: #60a5fa;
  }
  :root:not([data-theme="light"]) .stat-icon--purple {
    background: rgba(168, 85, 247, 0.22);
    color: #c084fc;
  }
  :root:not([data-theme="light"]) .stat-icon--green {
    background: rgba(16, 185, 129, 0.22);
    color: #34d399;
  }
  :root:not([data-theme="light"]) .search-input {
    background: var(--color-background-soft, #1e293b);
    border-color: var(--color-border, #475569);
    color: var(--color-heading, #ffffff);
  }
  :root:not([data-theme="light"]) .filter-tabs {
    background: var(--color-background-mute, #334155);
    border-color: var(--color-border, #475569);
  }
  :root:not([data-theme="light"]) .filter-tab.active {
    background: var(--card-bg, #1e293b);
    color: #60a5fa;
    border: 1px solid var(--color-border, #475569);
  }
  :root:not([data-theme="light"]) .table-card {
    background: var(--card-bg, #1e293b);
    border-color: var(--color-border, #334155);
  }
  :root:not([data-theme="light"]) .modern-table th {
    background: rgba(15, 23, 42, 0.65);
    color: var(--color-text-muted, #94a3b8);
    border-bottom: 2px solid var(--color-border, #334155);
  }
  :root:not([data-theme="light"]) .modern-table td {
    border-bottom-color: rgba(255, 255, 255, 0.07);
  }
  :root:not([data-theme="light"]) .group-tag {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.12);
    color: var(--color-text, #f1f5f9);
  }
  :root:not([data-theme="light"]) .group-tag--primary {
    background: rgba(37, 99, 235, 0.22);
    border-color: rgba(96, 165, 250, 0.35);
    color: #93c5fd;
  }
  :root:not([data-theme="light"]) .override-input {
    background: var(--color-background, #0f172a);
    border-color: var(--color-border, #475569);
    color: var(--color-heading, #ffffff);
  }
  :root:not([data-theme="light"]) .effective-custom {
    background: rgba(16, 185, 129, 0.2);
    color: #34d399;
    border-color: rgba(16, 185, 129, 0.35);
  }
  :root:not([data-theme="light"]) .effective-default {
    background: rgba(255, 255, 255, 0.06);
    color: var(--color-heading, #f1f5f9);
    border-color: var(--color-border, #475569);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .discounts-settings-container {
    padding: 0.75rem;
  }
  .table-toolbar {
    flex-direction: column;
    align-items: stretch;
  }
  .search-wrap {
    max-width: 100%;
  }
  .filter-tabs {
    overflow-x: auto;
  }
}
</style>
